import React from "react";
import {
    Compass,
    LineChart,
    Users,
    Workflow,
    Leaf,
    Handshake,
    ArrowUpRight,
} from "lucide-react";

const SERVICES = [
    {
        icon: Compass,
        title: "Strategy & Performance Diagnostics",
        text: "ISO 20700-aligned engagements to baseline performance, surface friction and translate ambition into a measurable plan.",
        anchor: "diagnostics",
    },
    {
        icon: Workflow,
        title: "Operational Excellence",
        text: "Lean Six Sigma Black Belt redesign of core processes — eliminate waste, stabilise quality and accelerate cycle times.",
        anchor: "ops",
    },
    {
        icon: Users,
        title: "Change & Adoption",
        text: "Prosci® ADKAR-based change management so people, leaders and teams move with the strategy — not against it.",
        anchor: "change",
    },
    {
        icon: LineChart,
        title: "Programme & Project Delivery",
        text: "PMP®-grade governance for transformations, integrations and growth initiatives, delivered with discipline and clarity.",
        anchor: "delivery",
    },
    {
        icon: Handshake,
        title: "Vested® Sourcing & Partnerships",
        text: "Outcome-based commercial models that align suppliers, partners and customers around shared, measurable value.",
        anchor: "vested",
    },
    {
        icon: Leaf,
        title: "Customer Experience & COPC®",
        text: "COPC®-grade customer operations design — service performance, contact centre uplift, and lifecycle experience.",
        anchor: "cx",
    },
];

export default function Services() {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="relative py-24 md:py-32 bg-white"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
                    <div className="lg:col-span-7">
                        <span
                            data-testid="services-eyebrow"
                            className="eyebrow text-bpo-cyanDark"
                        >
                            What we deliver
                        </span>
                        <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-bpo-ink leading-[1.05]">
                            Accessible, affordable systems —
                            <span className="block text-bpo-charcoal/80 font-bold">
                                built on certified standards.
                            </span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5">
                        <p className="text-base md:text-lg text-bpo-charcoal/80 leading-relaxed">
                            Six complementary practices, one operating model.
                            Every engagement is scoped to your stage and budget —
                            from a focused diagnostic to a multi-quarter
                            transformation programme.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {SERVICES.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <article
                                key={s.anchor}
                                data-testid={`service-card-${s.anchor}`}
                                className="group relative rounded-2xl border border-gray-200 bg-white p-7 hover:shadow-[0_18px_40px_-20px_rgba(31,41,55,0.18)] hover:-translate-y-0.5 transition-all overflow-hidden"
                            >
                                <span
                                    aria-hidden
                                    className="absolute top-0 left-0 right-0 h-0.5 bg-bpo-cyan scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500"
                                />
                                <div className="flex items-center justify-between mb-6">
                                    <div className="h-12 w-12 rounded-xl bg-bpo-cyanLight border border-bpo-cyan/30 flex items-center justify-center text-bpo-cyanDark">
                                        <Icon className="h-6 w-6" />
                                    </div>
                                    <span className="font-display text-sm font-bold text-gray-300">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                </div>
                                <h3 className="font-display text-xl md:text-2xl font-extrabold tracking-tight text-bpo-ink leading-tight">
                                    {s.title}
                                </h3>
                                <p className="mt-3 text-[15px] text-bpo-charcoal/80 leading-relaxed">
                                    {s.text}
                                </p>
                                <a
                                    href="#contact"
                                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-bpo-ink group-hover:text-bpo-cyanDark transition-colors"
                                    data-testid={`service-cta-${s.anchor}`}
                                >
                                    Discuss this service
                                    <ArrowUpRight className="h-4 w-4" />
                                </a>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
